import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, UserPlus, CalendarClock, CreditCard, ScrollText, Leaf, Mail,
  Briefcase, Coins, Heart, HeartPulse, Users, Plane, CalendarDays, Lightbulb, ShieldAlert,
  Star, Check, Gift, Trophy, ChevronDown, Quote, Moon, Phone,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StarField } from "@/components/StarField";
import { PlansGrid } from "@/components/PlansGrid";
import { ConsultSection } from "@/components/ConsultSection";
import { Disclaimer } from "@/components/Disclaimer";
import { ZodiacStrip, ZodiacWheel } from "@/components/Zodiac";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const STEPS = [
  { icon: UserPlus, title: "Create Account", desc: "Sign up in under a minute with your basic details." },
  { icon: CalendarClock, title: "Enter Birth Details", desc: "Date, time and place of birth for a precise chart." },
  { icon: CreditCard, title: "Choose Your Plan", desc: "Monthly, 3, 6 or 12 months — from ₹499. Cancel anytime." },
  { icon: ScrollText, title: "Receive Predictions", desc: "Personalized monthly guidance across your life." },
  { icon: Leaf, title: "Recommended Remedies", desc: "Simple mantras, rituals and lifestyle suggestions." },
  { icon: Mail, title: "Email & WhatsApp", desc: "Monthly updates delivered where you already are." },
];

const BENEFITS = [
  { icon: Briefcase, label: "Career & Business" },
  { icon: Coins, label: "Finance & Wealth" },
  { icon: Heart, label: "Love & Relationships" },
  { icon: HeartPulse, label: "Health & Well-being" },
  { icon: Users, label: "Family" },
  { icon: Plane, label: "Travel" },
  { icon: CalendarDays, label: "Important Dates" },
  { icon: Lightbulb, label: "Opportunities" },
  { icon: ShieldAlert, label: "Challenges" },
  { icon: Leaf, label: "Personalized Remedies" },
  { icon: Star, label: "Lucky Days & Favourable Periods" },
  { icon: Moon, label: "Things to Avoid" },
];

const PLAN_FEATURES = [
  "Personalized monthly astrology prediction",
  "Monthly remedies & rituals",
  "Email delivery",
  "WhatsApp delivery",
  "Personal account & dashboard",
  "Full prediction history",
  "Subscription management",
];

const DRAW_TERMS = [
  ["Eligibility", "Open to registered subscribers who maintain an eligible, continuously paid subscription."],
  ["24 Consecutive Months", "Requires 24 consecutive months of successfully paid subscription with no lapse."],
  ["Double Chance", "Subscribers on 3-Month, 6-Month or Annual plans get 2 entries (double chance) in the draw; Monthly plan subscribers get 1 entry."],
  ["Payment Requirement", "Each qualifying month must be a successful, non-refunded payment."],
  ["Draw Date / Status", "Draw date and status are published by the administrator and updated on your dashboard."],
  ["Winner Selection", "Winners are chosen via an auditable, randomized selection with a recorded seed and audit log."],
  ["Prize Details", "Current promotional prize is a 1 BHK apartment, configurable by the administrator to another approved prize."],
  ["Disqualification", "Missed payments, cancellation, refunds, fraud or breach of terms result in disqualification."],
  ["Compliance", "The program runs only subject to applicable Indian laws, tax and state-specific promotional rules."],
];

const PARTICLES = Array.from({ length: 8 }).map((_, i) => ({
  id: i, top: 15 + Math.random() * 70, left: Math.random() * 100, d: 7 + Math.random() * 6, size: Math.random() * 3 + 2,
}));

export default function Home() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cfg, setCfg] = useState({ monthly_price: 499, lucky_draw_prize: "1 BHK Apartment", lucky_draw_eligibility_months: 24, promo_banner: "" });
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/content/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/content/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const goStart = () => nav(user ? "/dashboard" : "/register");

  return (
    <div className="bg-[#080B1A] text-slate-100 overflow-x-hidden pb-[72px] lg:pb-0">
      <Navbar />

      {cfg.promo_banner ? (
        <div className="fixed top-[64px] inset-x-0 z-40 bg-amber-500/10 border-y border-amber-500/25 text-center py-2 px-4 text-xs text-amber-200 backdrop-blur">
          {cfg.promo_banner}
        </div>
      ) : null}

      {/* HERO */}
      <section className="relative min-h-[94vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-16 sm:pt-28 sm:pb-24 overflow-hidden bg-gradient-to-b from-[#080B1A] via-[#0E1229] to-[#12102B]">
        <StarField count={110} />
        <div className="orb w-[620px] h-[620px] -top-40 left-1/2 -translate-x-1/2 bg-indigo-700/15" />
        <div className="orb w-[360px] h-[360px] top-24 right-6 bg-amber-500/12" />
        <div className="orb w-[300px] h-[300px] bottom-0 left-0 bg-purple-700/12" />

        {/* zodiac wheel behind headline */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(760px,120vw)] h-[min(760px,120vw)] opacity-[0.5]">
          <ZodiacWheel className="w-full h-full" />
        </div>

        {/* floating particles */}
        {PARTICLES.map((p) => (
          <span key={p.id} className="absolute rounded-full bg-amber-200/70 drift"
            style={{ top: `${p.top}%`, left: `${p.left}%`, width: p.size, height: p.size, "--d": `${p.d}s`, boxShadow: "0 0 8px rgba(229,193,88,0.6)" }} />
        ))}

        <motion.div variants={fade} initial="hidden" animate="show" className="relative z-10 max-w-4xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/25 bg-[#0B1024]/60 px-4 py-1.5 text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.2em] text-amber-300/90 backdrop-blur pulse-glow">
            <Sparkles size={14} /> Your Stars. Your Path. Your Monthly Guidance.
          </span>
          <h1 className="mt-7 font-serif font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-7xl text-slate-50">
            Discover What the Stars <br className="hidden sm:block" /> Have in Store <span className="gold-text">for You.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Personalized monthly astrology predictions and traditional remedies — crafted from your
            unique birth details and delivered to your dashboard, email and WhatsApp.
          </p>
        </motion.div>

        <motion.div id="pricing" variants={fade} custom={1} initial="hidden" animate="show"
          className="relative z-10 w-full max-w-7xl mt-8 sm:mt-12 text-left scroll-mt-24" data-testid="hero-plans">
          <PlansGrid />
          <p className="mt-5 text-center text-xs text-slate-400">Plan prices shown; applicable 18% GST is displayed at checkout. Every plan includes lucky draw entry — 3, 6 &amp; 12-month plans get a <span className="text-amber-200 font-semibold">double chance</span>.</p>
        </motion.div>

        <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="relative z-10 max-w-4xl">
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button data-testid="hero-howitworks-button"
              onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-ghost-gold rounded-full px-7 py-3.5 text-base">
              How It Works
            </button>
          </div>
          <button data-testid="hero-consult-link"
            onClick={() => document.getElementById("consult")?.scrollIntoView({ behavior: "smooth" })}
            className="group mt-5 inline-flex items-center gap-2 text-sm text-amber-200/90 hover:text-amber-100 transition-colors">
            <Phone size={15} /> Or consult an astrologer personally
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </button>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><Check size={14} className="text-amber-300" /> Cancel anytime</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-amber-300" /> Private & secure</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-amber-300" /> Email + WhatsApp</span>
          </div>
          <div className="mt-9 max-w-2xl mx-auto">
            <Disclaimer compact />
          </div>
        </motion.div>
      </section>

      <ZodiacStrip />

      {/* HOW IT WORKS */}
      <Section id="how-it-works" eyebrow="How MyssticChat Works" title="Six simple steps to monthly clarity">
        <div className="relative grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} custom={i} variants={fade} initial="hidden" whileInView="show"
              viewport={{ once: true, margin: "-60px" }} className="group mc-card p-5 sm:p-6 relative overflow-hidden flex sm:block items-start gap-4"
              data-testid={`how-step-${i}`}>
              <span className="absolute -right-4 -top-6 font-serif text-7xl text-amber-500/5 group-hover:text-amber-500/10 transition-colors select-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="relative flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/20 to-purple-500/10 border border-amber-500/25 text-amber-300">
                <s.icon size={20} />
              </span>
              <div className="relative">
                <h3 className="sm:mt-5 font-serif text-lg sm:text-xl text-slate-50">{s.title}</h3>
                <p className="mt-1 sm:mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* BENEFITS */}
      <Section id="benefits" eyebrow="What You Receive Every Month" title="A complete reading of the month ahead"
        subtitle="Twelve areas of life, decoded from your birth chart — plus remedies and lucky windows.">
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.label} custom={i} variants={fade} initial="hidden" whileInView="show"
              viewport={{ once: true }} className="group mc-card p-3.5 sm:p-5 flex items-center gap-3 sm:gap-4 min-w-0" data-testid={`benefit-${i}`}>
              <span className="flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/15 to-purple-500/10 border border-amber-500/20 text-amber-300 group-hover:scale-110 transition-transform">
                <b.icon size={19} />
              </span>
              <span className="text-xs sm:text-sm text-slate-200 leading-snug break-words min-w-0">{b.label}</span>
            </motion.div>
          ))}
        </div>
      </Section>

      <ConsultSection fee={cfg.consultation_base_fee ?? 999} gst={cfg.gst_percent ?? 18} />

      {/* LUCKY DRAW */}
      <section id="lucky-draw" className="relative py-14 sm:py-28 overflow-hidden">
        <StarField count={60} />
        <div className="orb w-[520px] h-[520px] top-10 left-1/2 -translate-x-1/2 bg-amber-500/8" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/25 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-amber-400/90">
              <Trophy size={15} /> 24-Month Subscriber Lucky Draw
            </span>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-50">
              Stay Connected for 24 Months. <span className="gold-text">Unlock a Chance to Win.</span>
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Subscribers who maintain an eligible subscription for <strong className="text-amber-200">24 consecutive months</strong> become
              eligible for a promotional lucky draw for a <strong className="text-amber-200">{cfg.lucky_draw_prize}</strong>.
              3, 6 &amp; 12-month plan subscribers get a <strong className="text-amber-200">double chance</strong>.
              Eligibility and prize rules are subject to applicable laws and the published terms. Winning is not guaranteed.
            </p>
          </div>

          <div className="mt-8 sm:mt-12 grid gap-3 sm:gap-4 sm:grid-cols-2">
            {DRAW_TERMS.map(([h, d], i) => (
              <div key={h} className="mc-card p-5 flex gap-4" data-testid={`draw-term-${i}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 shrink-0">
                  <Gift size={16} />
                </span>
                <div>
                  <h4 className="text-sm font-semibold text-amber-100">{h}</h4>
                  <p className="mt-1 text-sm text-slate-400 leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-amber-300/90">Terms &amp; Conditions Apply.</p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Section eyebrow="Testimonials" title="Trusted by seekers across India">
        <div className="flex md:grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 pb-2" data-testid="testimonials-row">
          {testimonials.map((t, i) => (
            <motion.div key={i} custom={i} variants={fade} initial="hidden" whileInView="show"
              viewport={{ once: true }} className="mc-card p-6 flex flex-col min-w-[84%] md:min-w-0 snap-center" data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-1 text-amber-300">
                {Array.from({ length: 5 }).map((_, s) => <Star key={s} size={14} fill="currentColor" />)}
              </div>
              <Quote size={22} className="mt-4 text-amber-400/40" />
              <p className="mt-2 text-sm text-slate-300 leading-relaxed flex-1">{t.text}</p>
              <div className="mt-5 hairline pt-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-500/30 to-purple-500/20 border border-amber-400/40 font-serif text-amber-100">
                  {t.name?.[0]}
                </span>
                <div>
                  <p className="text-sm font-semibold text-amber-100">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.sign} · {t.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" eyebrow="FAQ" title="Questions, answered">
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="mc-card overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid={`faq-toggle-${i}`}>
                <span className="text-sm sm:text-base font-medium text-slate-100">{f.q}</span>
                <ChevronDown size={18} className={`text-amber-300 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              <div className={`grid transition-[grid-template-rows] duration-300 ${openFaq === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                  <div className="px-5 pb-5 -mt-1 text-sm text-slate-400 leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA strip */}
      <section className="relative py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="relative p-[1px] rounded-3xl bg-gradient-to-r from-amber-500/40 via-amber-200/20 to-purple-500/30">
            <div className="relative rounded-3xl bg-[#0E132A] px-6 sm:px-8 py-14 text-center overflow-hidden">
              <StarField count={40} />
              <div className="orb w-72 h-72 -bottom-20 left-1/2 -translate-x-1/2 bg-amber-500/10" />
              <ZodiacWheel className="absolute -right-16 -top-16 w-56 h-56 opacity-40 hidden sm:block" />
              <h2 className="relative z-10 font-serif text-3xl sm:text-4xl text-slate-50">Begin your monthly guidance today</h2>
              <p className="relative z-10 mt-3 text-slate-400">Personalized predictions & remedies for ₹{cfg.monthly_price}/month.</p>
              <div className="relative z-10 mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button data-testid="cta-strip-button" onClick={goStart}
                  className="btn-gold rounded-full px-8 py-3.5 inline-flex items-center gap-2">
                  Start My Astrology Journey <ArrowRight size={18} />
                </button>
                <button data-testid="cta-strip-consult-button"
                  onClick={() => document.getElementById("consult")?.scrollIntoView({ behavior: "smooth" })}
                  className="btn-ghost-gold rounded-full px-7 py-3.5 inline-flex items-center gap-2">
                  <Phone size={16} /> Consult an Astrologer
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-amber-500/20 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3">
        <div className="flex-1">
          <p className="text-xs text-slate-400">Plans from</p>
          <p className="font-serif text-lg text-amber-100 leading-none">₹{cfg.monthly_price}/mo</p>
        </div>
        <button data-testid="sticky-consult-button" aria-label="Consult an astrologer"
          onClick={() => document.getElementById("consult")?.scrollIntoView({ behavior: "smooth" })}
          className="btn-ghost-gold rounded-full h-10 w-10 flex items-center justify-center">
          <Phone size={16} />
        </button>
        <button data-testid="sticky-subscribe-button" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="btn-gold rounded-full px-5 py-2.5 text-sm">
          View Plans
        </button>
      </div>
    </div>
  );
}

function Section({ id, eyebrow, title, subtitle, children }) {
  return (
    <section id={id} className="relative py-14 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 sm:mb-12 text-center max-w-2xl mx-auto">
          <div className="ornament mb-4"><Sparkles size={14} /></div>
          <span className="text-xs uppercase tracking-[0.2em] text-amber-400/90">{eyebrow}</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-50">{title}</h2>
          {subtitle && <p className="mt-3 text-sm text-slate-400 leading-relaxed">{subtitle}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}
